import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { absCenter } from '@sorare/core/src/lib/style';

import streakBackground from 'assets/streakBackground.jpg';
import { StreakProgressBar } from 'components/missions/Streaks/StreakProgressBar';

import type { LiveStreakProgress_so5Lineup } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  height: 100%;
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  backdrop-filter: blur(32px);

  &.reached {
    border: 1px solid #fef314;
    box-shadow:
      0 0 4px 0 rgba(254, 193, 24, 0.25) inset,
      0 0 8px 0 rgba(255, 78, 34, 0.5);
    backdrop-filter: blur(32px);
    background: url(${streakBackground}) center/cover no-repeat;
  }
`;

const Sup = styled(BrandXXS).attrs({ as: 'sup' })`
  vertical-align: top;
`;

const LineThroughLabelS = styled(BrandS)`
  text-decoration: line-through;
`;

const FlameContainer = styled(Horizontal).attrs({ center: true })`
  position: relative;
`;

const CenteredNumber = styled(BrandXXS)`
  text-shadow:
    0 -3px 4.5px rgba(0, 0, 0, 0.6),
    0 0 4.5px var(--c-limited-tertiary);

  ${absCenter}
`;

type Props = {
  so5Lineup: LiveStreakProgress_so5Lineup;
};

export const LiveStreakProgress = ({ so5Lineup }: Props) => {
  const so5Ranking = so5Lineup?.so5Rankings.at(0);
  const currentScore = so5Ranking?.score || 0;
  if (!so5Lineup?.myThresholdsStreakTask) return null;
  return (
    <Root
      className={classNames({
        reached:
          currentScore >=
            so5Lineup.myThresholdsStreakTask.currentThreshold.score &&
          so5Lineup.myThresholdsStreakTask.completedBy?.id === so5Lineup.id,
      })}
    >
      <SBHorizontal>
        <Horizontal gap={0.5}>
          <FlameContainer>
            <Flame />
            <CenteredNumber>
              {so5Lineup.myThresholdsStreakTask.progress + 1}
            </CenteredNumber>
          </FlameContainer>
          {so5Lineup?.myThresholdsStreakTask.currentThreshold.originalScore ? (
            <>
              <BrandS
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color':
                      'linear-gradient(323deg, #D49E3C 14.3%, #FF4E22 85.7%)',
                  } as CSSProperties
                }
              >
                {so5Lineup.myThresholdsStreakTask.currentThreshold.score}
              </BrandS>
              <LineThroughLabelS color="var(--c-nd-600)">
                {
                  so5Lineup.myThresholdsStreakTask.currentThreshold
                    .originalScore
                }
              </LineThroughLabelS>
            </>
          ) : (
            <BrandS>
              {so5Lineup.myThresholdsStreakTask.currentThreshold.score}
            </BrandS>
          )}
        </Horizontal>
        <BrandS>
          <FormattedMessage
            {...fantasy.ordinalPoints}
            values={{
              score: (
                <FormattedNumber
                  value={so5Ranking?.score || 0}
                  maximumFractionDigits={2}
                />
              ),
              sup: c => <Sup color="var(--c-nd-600)">{c}</Sup>,
            }}
          />
        </BrandS>
      </SBHorizontal>
      <StreakProgressBar
        currentThresholdScore={
          so5Lineup.myThresholdsStreakTask.currentThreshold.score
        }
        lineup={so5Lineup}
      />
    </Root>
  );
};

LiveStreakProgress.fragments = {
  so5Lineup: gql`
    fragment LiveStreakProgress_so5Lineup on So5Lineup {
      id
      so5Rankings {
        id
        score
      }
      myThresholdsStreakTask {
        id
        progress
        completedBy {
          id
        }
        currentThreshold {
          score
          originalScore
        }
      }
      ...StreakProgressBar_so5Lineup
    }
    ${StreakProgressBar.fragments.so5Lineup}
  ` as TypedDocumentNode<LiveStreakProgress_so5Lineup>,
};
